/**
 * Demo Department Step — the wonder moment.
 *
 * This is where the customer explores a fully functioning marketing
 * department. Every tab is real content pulled from the demo registry.
 * A single, unmissable CTA sits at the top-right at all times:
 * "Make This Mine". The tab bar is designed to feel like the real
 * Orkexra product — because the moment they click the CTA, it will
 * become the real product.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { previewDemo, type DemoFullShape, type GenesisSession } from "@/entities/genesis";
import { Button } from "@/shared/ui/button";

interface Props {
  session: GenesisSession;
  onMakeItMine: () => void;
  onTick: (secondsInDemo: number) => void;
}

type TabKey = "overview" | "campaigns" | "assets" | "calendar" | "brand" | "business" | "knowledge" | "insights";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "campaigns", label: "Campaigns" },
  { key: "assets", label: "Assets" },
  { key: "calendar", label: "Calendar" },
  { key: "brand", label: "Brand Brain" },
  { key: "business", label: "Business Brain" },
  { key: "knowledge", label: "Knowledge" },
  { key: "insights", label: "Intelligence" },
];

export function DemoDepartmentStep({ session, onMakeItMine, onTick }: Props): JSX.Element {
  const demoKey = session.demo_key || "coffee_shop";
  const dq = useQuery({
    queryKey: ["genesis", "demo", demoKey],
    queryFn: () => previewDemo(demoKey),
    staleTime: Infinity,
  });
  const [tab, setTab] = useState<TabKey>("overview");

  useEffect(() => {
    const start = Date.now();
    const int = setInterval(() => {
      const s = Math.floor((Date.now() - start) / 1000);
      // Only ping the server every 15 seconds to save chatter.
      if (s > 0 && s % 15 === 0) onTick(s);
    }, 1000);
    return () => clearInterval(int);
  }, [onTick]);

  if (dq.isPending || !dq.data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-[#f5efe6]/70">
        Preparing your demo department…
      </div>
    );
  }
  const demo = dq.data;

  return (
    <div className="space-y-8" data-testid="demo-department-step">
      {/* Intelly welcome */}
      <div className="rounded-3xl border border-[#f5efe6]/8 bg-gradient-to-br from-[#131a17] to-[#0f1613] p-6 md:p-8">
        <div className="flex items-start gap-5">
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#c7a25a]/40 bg-[#c7a25a]/12 font-serif text-lg italic text-[#c7a25a]"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            i
          </div>
          <div className="flex-1 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">
              Intelly · Chief of Staff
            </p>
            <p className="text-[15px] leading-relaxed text-[#f5efe6]/90 md:text-[16px]">
              Hi. I'm Intelly. I've prepared a complete marketing department for
              <span className="italic text-[#c7a25a]"> {demo.display_name}</span> so
              you can experience what's possible.
            </p>
            <p className="text-[14px] leading-relaxed text-[#f5efe6]/60">
              Take a look around. When you're ready, we'll make it yours.
            </p>
          </div>
        </div>
      </div>

      {/* Tab bar + Make This Mine */}
      <div className="sticky top-0 z-20 -mx-2 flex flex-col gap-4 rounded-3xl border border-[#f5efe6]/8 bg-[#0d1310]/90 px-4 py-3 backdrop-blur md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto" data-testid="demo-tab-bar">
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`demo-tab-${t.key}`}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] transition ${
                  active
                    ? "bg-[#f5efe6]/10 text-[#f5efe6]"
                    : "text-[#f5efe6]/60 hover:text-[#f5efe6]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <Button
          onClick={onMakeItMine}
          data-testid="demo-make-this-mine"
          className="group h-11 rounded-full bg-[#c7a25a] px-5 text-[14px] font-medium text-[#0d1310] shadow-[0_16px_40px_-16px_rgba(199,162,90,0.7)] hover:bg-[#d4b06e]"
        >
          <span aria-hidden className="mr-2">✨</span>
          Make This Mine
        </Button>
      </div>

      {/* Tab content */}
      <div className="min-h-[420px]" data-testid={`demo-panel-${tab}`}>
        {tab === "overview" && <Overview demo={demo} />}
        {tab === "campaigns" && <Campaigns demo={demo} />}
        {tab === "assets" && <Assets demo={demo} />}
        {tab === "calendar" && <Calendar demo={demo} />}
        {tab === "brand" && <BrandBrainPanel demo={demo} />}
        {tab === "business" && <BusinessBrainPanel demo={demo} />}
        {tab === "knowledge" && <KnowledgePanel demo={demo} />}
        {tab === "insights" && <InsightsPanel demo={demo} />}
      </div>

      <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#f5efe6]/30">
        Nothing here is real — yet. Every asset is a template until you make it yours.
      </p>
    </div>
  );
}

// ── Panels ───────────────────────────────────────────────────────
function Overview({ demo }: { demo: DemoFullShape }) {
  const active = demo.campaigns.filter((c) => c.status === "active").length;
  const drafts = demo.assets.filter((a) => a.status === "draft" || a.status === "needs_review").length;
  const scheduled = demo.calendar.length;
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <PanelCard title="This department at a glance" accent={demo.color_accent}>
        <p className="font-serif text-2xl italic leading-tight text-[#f5efe6]">"{demo.hero_tagline}"</p>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          <Metric label="Active campaigns" value={active} />
          <Metric label="Drafts + reviews" value={drafts} />
          <Metric label="Scheduled" value={scheduled} />
        </div>
      </PanelCard>
      <PanelCard title="What Intelly is watching" accent={demo.color_accent}>
        <ul className="space-y-3">
          {demo.insights.slice(0, 2).map((i) => (
            <li key={i.id} className="rounded-2xl border border-[#f5efe6]/6 bg-[#0d1310] px-4 py-3">
              <p className="text-[13px] font-medium text-[#f5efe6]">{i.title}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-[#f5efe6]/70">{i.body}</p>
            </li>
          ))}
        </ul>
      </PanelCard>
    </div>
  );
}

function Campaigns({ demo }: { demo: DemoFullShape }) {
  return (
    <div className="grid gap-4">
      {demo.campaigns.map((c) => (
        <PanelCard key={c.id} accent={demo.color_accent}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">
                {c.status}
              </p>
              <h3 className="mt-1 font-serif text-xl text-[#f5efe6]">{c.name}</h3>
              <p className="mt-1 text-[13px] text-[#f5efe6]/70">{c.objective}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.channels.map((ch) => (
                <span key={ch} className="rounded-full border border-[#f5efe6]/12 px-2 py-0.5 text-[11px] text-[#f5efe6]/60">
                  {ch}
                </span>
              ))}
            </div>
          </div>
          <p className="mt-4 border-t border-[#f5efe6]/6 pt-3 text-[13px] leading-relaxed text-[#f5efe6]/70">
            {c.ai_summary}
          </p>
        </PanelCard>
      ))}
    </div>
  );
}

function Assets({ demo }: { demo: DemoFullShape }) {
  const groups = useMemo(() => {
    const g: Record<string, typeof demo.assets> = {};
    demo.assets.forEach((a) => { (g[a.kind] ||= []).push(a); });
    return g;
  }, [demo.assets]);
  return (
    <div className="space-y-6">
      {Object.entries(groups).map(([kind, items]) => (
        <div key={kind}>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">{kind}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {items.map((a) => (
              <PanelCard key={a.id} accent={demo.color_accent}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[14px] font-medium text-[#f5efe6]">{a.title}</p>
                  <span className="rounded-full border border-[#f5efe6]/12 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#f5efe6]/60">
                    {a.status}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-[#f5efe6]/70">
                  {a.body}
                </p>
              </PanelCard>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Calendar({ demo }: { demo: DemoFullShape }) {
  return (
    <PanelCard accent={demo.color_accent}>
      <div className="grid gap-3">
        {demo.calendar.map((c) => (
          <div key={c.id} className="grid grid-cols-[100px_1fr_120px] items-center gap-4 rounded-xl border border-[#f5efe6]/6 px-4 py-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#c7a25a]">{c.when}</p>
            <p className="text-[13px] text-[#f5efe6]/90">{c.channel}</p>
            <span className="justify-self-end rounded-full border border-[#f5efe6]/12 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#f5efe6]/60">
              {c.status}
            </span>
          </div>
        ))}
      </div>
    </PanelCard>
  );
}

function BrandBrainPanel({ demo }: { demo: DemoFullShape }) {
  const b = demo.brand_brain;
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <PanelCard title="Voice" accent={demo.color_accent}>
        <p className="text-[14px] leading-relaxed text-[#f5efe6]/85">{b.voice}</p>
        <p className="mt-4 font-serif text-lg italic text-[#c7a25a]">"{b.tagline}"</p>
      </PanelCard>
      <PanelCard title="Identity Pillars" accent={demo.color_accent}>
        <ul className="space-y-2">
          {b.identity_pillars.map((p, i) => (
            <li key={i} className="text-[14px] leading-relaxed text-[#f5efe6]/85">— {p}</li>
          ))}
        </ul>
      </PanelCard>
      <PanelCard title="Do use" accent={demo.color_accent}>
        <div className="flex flex-wrap gap-1.5">
          {b.do_use.map((w) => (
            <span key={w} className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1 text-[12px] text-emerald-100">{w}</span>
          ))}
        </div>
      </PanelCard>
      <PanelCard title="Don't use" accent={demo.color_accent}>
        <div className="flex flex-wrap gap-1.5">
          {b.dont_use.map((w) => (
            <span key={w} className="rounded-full border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[12px] text-rose-100">{w}</span>
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

function BusinessBrainPanel({ demo }: { demo: DemoFullShape }) {
  const b = demo.business_brain;
  const rows: [string, string][] = [
    ["Mission", b.mission],
    ["Ideal customer", b.ideal_customer],
    ["Core offer", b.core_offer],
    ["Unique advantage", b.unique_advantage],
    ["Revenue model", b.revenue_model],
    ["Growth stage", b.growth_stage],
  ];
  return (
    <PanelCard accent={demo.color_accent}>
      <div className="grid gap-4 md:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">{k}</p>
            <p className="mt-1.5 text-[14px] leading-relaxed text-[#f5efe6]/85">{v}</p>
          </div>
        ))}
      </div>
    </PanelCard>
  );
}

function KnowledgePanel({ demo }: { demo: DemoFullShape }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {demo.knowledge.map((k) => (
        <PanelCard key={k.id} accent={demo.color_accent}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">{k.source}</p>
          <h4 className="mt-1 text-[15px] font-medium text-[#f5efe6]">{k.title}</h4>
          <p className="mt-2 text-[13px] leading-relaxed text-[#f5efe6]/70">{k.excerpt}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {k.tags.map((t) => (
              <span key={t} className="rounded-full border border-[#f5efe6]/10 px-2 py-0.5 text-[11px] text-[#f5efe6]/60">{t}</span>
            ))}
          </div>
        </PanelCard>
      ))}
    </div>
  );
}

function InsightsPanel({ demo }: { demo: DemoFullShape }) {
  return (
    <div className="grid gap-3">
      {demo.insights.map((i) => (
        <PanelCard key={i.id} accent={demo.color_accent}>
          <div className="flex items-start gap-3">
            <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
              i.priority === "high" ? "bg-[#c7a25a]" : i.priority === "medium" ? "bg-amber-300" : "bg-[#f5efe6]/40"
            }`} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">{i.priority} priority</p>
              <h4 className="mt-1 text-[15px] font-medium text-[#f5efe6]">{i.title}</h4>
              <p className="mt-2 text-[14px] leading-relaxed text-[#f5efe6]/80">{i.body}</p>
            </div>
          </div>
        </PanelCard>
      ))}
    </div>
  );
}

// ── Building blocks ─────────────────────────────────────────────
function PanelCard({ title, children, accent }: { title?: string; children: React.ReactNode; accent?: string }) {
  return (
    <div className="rounded-3xl border border-[#f5efe6]/8 bg-[#0f1613]/70 p-5 md:p-6" style={{ boxShadow: `0 20px 60px -30px ${accent ?? "#000"}55` }}>
      {title && (
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">{title}</p>
      )}
      {children}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="font-serif text-3xl text-[#c7a25a]" style={{ fontFamily: "'Fraunces', serif" }}>{value}</p>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-[#f5efe6]/50">{label}</p>
    </div>
  );
}
